import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Search,
  Plus,
  FileText,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Building2,
  Car,
  Download,
  RefreshCw,
  Shield,
  XCircle,
  AlertCircle,
  Info,
} from 'lucide-react';
import { mockUser } from '../data/mock';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Source Health Panel ──────────────────────────────────
const STATUS_CONFIG = {
  FOUND:               { label: 'Operativo',          icon: <CheckCircle className="h-4 w-4" />, cls: 'text-green-600 bg-green-50 border-green-200' },
  NOT_FOUND:           { label: 'Operativo',          icon: <CheckCircle className="h-4 w-4" />, cls: 'text-green-600 bg-green-50 border-green-200' },
  CAPTCHA_REQUERIDO:   { label: 'CAPTCHA bloqueado',   icon: <AlertTriangle className="h-4 w-4" />, cls: 'text-yellow-700 bg-yellow-50 border-yellow-300' },
  CLOUDFLARE:          { label: 'Cloudflare bloquea',  icon: <AlertTriangle className="h-4 w-4" />, cls: 'text-yellow-700 bg-yellow-50 border-yellow-300' },
  ACCESO_BLOQUEADO:    { label: '403 Bloqueado',       icon: <XCircle className="h-4 w-4" />, cls: 'text-red-600 bg-red-50 border-red-200' },
  PORTAL_NO_DISPONIBLE:{ label: 'No disponible',       icon: <AlertCircle className="h-4 w-4" />, cls: 'text-gray-500 bg-gray-50 border-gray-200' },
  NO_IMPLEMENTADO:     { label: 'Sin endpoint',        icon: <Info className="h-4 w-4" />, cls: 'text-gray-500 bg-gray-50 border-gray-200' },
  NEVER_CHECKED:       { label: 'Sin verificar',       icon: <Clock className="h-4 w-4" />, cls: 'text-gray-400 bg-gray-50 border-gray-100' },
};

const SourceHealthPanel = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);

  const fetchHealth = async () => {
    try {
      const token = localStorage.getItem('nika-auth-token');
      const { data } = await axios.get(`${API}/api/health/sources`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSources(data.sources || []);
    } catch (_) {
      // Unauthenticated fallback — still show panel
    } finally {
      setLoading(false);
    }
  };

  const triggerProbe = async () => {
    setProbing(true);
    try {
      const token = localStorage.getItem('nika-auth-token');
      await axios.post(`${API}/api/health/sources/probe`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTimeout(fetchHealth, 5000); // refresh after 5s
    } finally {
      setTimeout(() => setProbing(false), 5000);
    }
  };

  useEffect(() => { fetchHealth(); }, []);

  const alerts = sources.filter(s => s.alert);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-4 w-4 text-blue-600" />
            Estado de Fuentes
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={triggerProbe} disabled={probing} className="h-7 px-2">
            <RefreshCw className={`h-3.5 w-3.5 ${probing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <CardDescription className="text-xs">
          Monitoreo en tiempo real de portales oficiales
        </CardDescription>
        {alerts.length > 0 && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span><strong>{alerts.length} fuente(s) requieren revisión técnica:</strong> {alerts.map(a => a.scraper.split('—')[0]).join(', ')}</span>
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="text-xs text-gray-400 text-center py-4">Cargando estado...</div>
        ) : (
          <div className="space-y-2">
            {sources.map((s, i) => {
              const cfg = STATUS_CONFIG[s.last_status] || STATUS_CONFIG['NEVER_CHECKED'];
              return (
                <div key={i} className={`flex items-start gap-2 p-2 rounded-lg border text-xs ${cfg.cls}`}>
                  <div className="flex-shrink-0 mt-0.5">{cfg.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate" title={s.scraper}>
                      {s.scraper.split('—')[0].trim()}
                    </div>
                    <div className="opacity-75 mt-0.5">{cfg.label}</div>
                    {s.alert && (
                      <div className="text-red-700 font-medium mt-0.5">
                        ⚠ {s.consecutive_failures} fallos consecutivos
                      </div>
                    )}
                  </div>
                  <a href={s.portal_url} target="_blank" rel="noopener noreferrer"
                    className="flex-shrink-0 text-blue-500 hover:underline mt-0.5" title="Abrir portal">
                    ↗
                  </a>
                </div>
              );
            })}
            {sources.length === 0 && (
              <div className="text-xs text-gray-400 text-center py-4">
                Sin datos. Presiona ↺ para verificar.
              </div>
            )}
          </div>
        )}
        <div className="mt-3 text-xs text-gray-400 text-center">
          Actualizado: {new Date().toLocaleTimeString('es-CO')}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Main Dashboard ───────────────────────────────────────
const Dashboard = ({ user = mockUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [validations, setValidations] = useState([]);
  const [loadingValidations, setLoadingValidations] = useState(true);
  const navigate = useNavigate();

  // Fetch real validations from backend
  useEffect(() => {
    const fetchValidations = async () => {
      try {
        const token = localStorage.getItem('nika-auth-token');
        if (!token) { setLoadingValidations(false); return; }
        const { data } = await axios.get(`${API}/api/validations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = (data?.validations || []).map((v) => {
          const r = v.result || {};
          const findings = r.findings || [];
          const score = r.score ?? 100;
          let status = 'review';
          if (score >= 90) status = 'approved';
          else if (score < 70) status = 'rejected';
          return {
            id: v.id,
            type: v.type, // 'person' | 'company' | 'vehicle'
            name: r.raw_data?.full_name || v.request_data?.first_name
              ? `${v.request_data?.first_name || ''} ${v.request_data?.last_name || ''}`.trim()
              : (v.request_data?.company_name || v.request_data?.plate || 'Consulta'),
            document: v.request_data?.document_number || v.request_data?.nit || v.request_data?.plate || '—',
            date: v.created_at,
            findings: findings.length,
            status,
            riskLevel: r.risk_level || 'low',
            raw: v,
          };
        });
        // Sort by date desc (already sorted by API but just in case)
        items.sort((a, b) => new Date(b.date) - new Date(a.date));
        setValidations(items);
      } catch (err) {
        console.error('Error loading validations:', err);
      } finally {
        setLoadingValidations(false);
      }
    };
    fetchValidations();
  }, []);

  const handleValidationClick = (item) => {
    // Open detail page reusing the full-report renderer
    navigate('/reporte-completo', {
      state: {
        result: item.raw.result,
        tipo: item.type,
        datos: item.raw.request_data || {},
        validation_id: item.id,
      },
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-50 border-green-200';
      case 'rejected': return 'text-red-600 bg-red-50 border-red-200';
      case 'review': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'person': return <Users className="h-4 w-4" />;
      case 'company': return <Building2 className="h-4 w-4" />;
      case 'vehicle': return <Car className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const isUnlimited = !user.consultationsLimit || user.consultationsLimit >= 999_999;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Bienvenido, {user.name}
          </h1>
          <p className="text-gray-600">
            Gestiona tus validaciones y monitorea el estado de tus consultas
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Consultas Realizadas</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="consultations-used">{user.consultationsUsed}</div>
              <p className="text-xs text-muted-foreground">
                {isUnlimited ? 'Acceso ilimitado' : `de ${user.consultationsLimit} disponibles`}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Acceso</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="access-status">
                {isUnlimited ? 'Ilimitado' : user.plan}
              </div>
              <p className="text-xs text-muted-foreground">
                Activo desde {new Date(user.joinDate).toLocaleDateString()}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Validaciones Hoy</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">12</div>
              <p className="text-xs text-muted-foreground">
                +3 desde ayer
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Alertas Activas</CardTitle>
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">2</div>
              <p className="text-xs text-muted-foreground">
                Requieren atención
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Usage Progress — solo se muestra cuando hay un límite real */}
        {!isUnlimited && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Uso de Consultas Mensual
              <Badge variant={(user.consultationsUsed / user.consultationsLimit) * 100 > 80 ? "destructive" : "secondary"}>
                {((user.consultationsUsed / user.consultationsLimit) * 100).toFixed(1)}% usado
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={(user.consultationsUsed / user.consultationsLimit) * 100} className="w-full" />
            <div className="flex justify-between text-sm text-gray-600 mt-2">
              <span>{user.consultationsUsed} utilizadas</span>
              <span>{user.consultationsLimit - user.consultationsUsed} restantes</span>
            </div>
          </CardContent>
        </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Acciones Rápidas</CardTitle>
                <CardDescription>
                  Inicia una nueva validación o consulta
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Button className="h-20 flex flex-col items-center justify-center space-y-2">
                    <Users className="h-6 w-6" />
                    <span>Validar Persona</span>
                  </Button>
                  <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2">
                    <Building2 className="h-6 w-6" />
                    <span>Validar Empresa</span>
                  </Button>
                  <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2">
                    <Car className="h-6 w-6" />
                    <span>Validar Vehículo</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recent Validations */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Validaciones Recientes</CardTitle>
                    <CardDescription>
                      Historial de tus últimas consultas
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Exportar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {loadingValidations && (
                    <div className="text-center py-8 text-gray-500">Cargando consultas…</div>
                  )}
                  {!loadingValidations && validations.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      Aún no has realizado consultas. Inicia una nueva validación arriba.
                    </div>
                  )}
                  {!loadingValidations && validations.slice(0, 6).map((validation) => (
                    <div
                      key={validation.id}
                      onClick={() => handleValidationClick(validation)}
                      data-testid={`validation-row-${validation.id}`}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-white rounded-lg">
                          {getTypeIcon(validation.type)}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {validation.name || '—'}
                          </div>
                          <div className="text-sm text-gray-600">
                            {validation.document}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(validation.date).toLocaleString('es-CO')} • {validation.findings} hallazgos
                          </div>
                        </div>
                      </div>

                      <div className="text-right space-y-1">
                        <Badge className={getStatusColor(validation.status)}>
                          {validation.status === 'approved' ? 'Aprobado' :
                           validation.status === 'rejected' ? 'Rechazado' : 'En Revisión'}
                        </Badge>
                        <div className={`text-xs font-medium ${getRiskColor(validation.riskLevel)}`}>
                          Riesgo {validation.riskLevel === 'low' ? 'Bajo' :
                                   validation.riskLevel === 'medium' ? 'Medio' : 'Alto'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {validations.length > 6 && (
                  <div className="mt-4 text-center">
                    <Button variant="outline" onClick={() => navigate('/historial')} data-testid="see-all-validations-btn">
                      Ver todas las validaciones ({validations.length})
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            
            {/* Quick Search */}
            <Card>
              <CardHeader>
                <CardTitle>Búsqueda Rápida</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por documento..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Button className="w-full mt-3">
                  Buscar
                </Button>
              </CardContent>
            </Card>

            {/* API Integration */}
            <Card>
              <CardHeader>
                <CardTitle>Integración API</CardTitle>
                <CardDescription>
                  Conecta tu sistema con nuestra API
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">API Key:</span>
                    <Badge variant="outline">Configurada</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Llamadas hoy:</span>
                    <span className="font-medium">47</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Límite diario:</span>
                    <span className="font-medium">1,000</span>
                  </div>
                  <Button variant="outline" size="sm" className="w-full">
                    Ver documentación
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Sources Health Panel */}
            <SourceHealthPanel />

            {/* Tu acceso */}
            <Card>
              <CardHeader>
                <CardTitle>Tu Acceso</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600" data-testid="access-label">
                      {isUnlimited ? 'Ilimitado' : user.plan}
                    </div>
                    <div className="text-sm text-gray-600">
                      {isUnlimited ? 'Sin restricciones' : 'Plan actual'}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Consultas incluidas:</span>
                      <span className="font-medium">
                        {isUnlimited ? 'Ilimitadas' : user.consultationsLimit.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Soporte:</span>
                      <span className="font-medium">Prioritario</span>
                    </div>
                    <div className="flex justify-between">
                      <span>API incluida:</span>
                      <span className="font-medium">Sí</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;